<template>
	<div class="blueBanner">		
		<h1>{{ headerOneContent }}</h1>
		<div class="wrapper">
			<div class="info easy">
				<span class="icon" />
				<span class="text">Easily notarize docs in minutes</span>
			</div>
			<div class="info secure">
				<span class="icon" />
				<span class="text">Guaranteed security and privacy</span>
			</div>
			<div class="info legal">
				<span class="icon" />
				<span class="text">Legal in all 50 states</span>
			</div>
		</div>
	</div>
	<div id="contentMain" class="mydoc2018 docSelect firstPage">
		<div class="myDocuments">
			<div class="myDocsTopBar">
				<h2 id="myDocsTitle" class="">
					Upload Documents to Start<hover-help>
						<template #mobileHelp>
							<help-content />
						</template>
						<template #hoverHelp>
							<help-content />
						</template>
					</hover-help>
				</h2>
			</div>
			<document-list />
			<div v-if="isEmpty" class="listOfLinks">
				Or start with a template:
				<a href="/contracts/affidavit-form/?loc=US"> Affidavit, </a>
				<a href="/contracts/living-trust/?loc=US">Living Trust, </a>
				<a href="/contracts/power-of-attorney-form/?loc=US">Power of Attorney, </a>
				<a href="/contracts/child-travel-consent/?loc=US">Child Travel Consent, </a>
				<a href="/contracts/medical-consent-form/?loc=US">Child Medical Consent, </a>
				<a href="/contracts/quit-claim-deed-form/?loc=US">Quitclaim Deed</a> 
			</div>
			<the-sticky-cta v-if="!isEmpty">
				<a href="#" class="largeProceedButton" @click="proceed">START NOTARIZATION</a>
			</the-sticky-cta>
		</div>		
	</div>
	<div class="protectedBy">
		<div class="wrapper">
			<span class="text">Protected By</span>
			<div class="logoWrapper">
				<span class="idenTrust" />
				<span class="IDology" />
			</div>
		</div>
	</div>
</template>

<script>
import DocumentList from "./DocumentList.vue";
import TheStickyCta from "../../components/ui/TheStickyCta.vue";
import { mapGetters, mapState, mapActions, mapMutations } from "vuex";
import { isUserSignedIn } from "../../helpers/signin";
import { PageAuthorization } from "../../mixins/PageAuthorization";
import { submitTrackingEvent } from "../../helpers/tracking";
import HoverHelp from "../../components/ui/HoverHelp.vue";
import HelpContent from "./HelpContent.vue";
import { statesMap } from "../../helpers/statesMap";
import { ItemTileFade } from "../../mixins/ItemTileFade";
import { myDocuments, lawDepot } from "@ado/notarization-vue-common/src/API/vfs.api";
import { nanoid } from "nanoid";
import { signers as getSigners } from "@ado/notarization-vue-common/src/API/signers.api";


export default {
	components: { DocumentList, TheStickyCta, HoverHelp, HelpContent },
	mixins: [new PageAuthorization(), new ItemTileFade()],
	data() {
		return {
			hasIncomingState: false,
			stateName: ``
		};
	},
	computed: {
		isSignedIn() {
			return isUserSignedIn();			
		},
		headerOneContent() {
			if (this.hasIncomingState) {
				return `${this.stateName} Online Notary`;
			} else {
				return `Online Notary`;
			}
		},
		...mapGetters([`isEmpty`]),
		...mapState([`maxSelectedDocuments`, `uploadTypeEnum`, `uploadStatusEnum`]),
		...mapState(`API`, [`notarizationId`]),
		...mapState([`notarizationDocuments`])
	},
	mounted() {
		submitTrackingEvent(`Landing`, null);	
		this.checkIncomingStateParam();
		this.checkAndHandleIncoming();
	},
	methods: {
		proceed() {
			if (!this.isSignedIn) {
				this.goToCreateAccount();
			}
			if (this.isSignedIn) {
				getSigners(this.notarizationId).then((signers) => {
					if (signers.length > 0) {
						this.goToSignersList();
					} else {
						this.goToWhoIsSigning();
					}
				});
			}
		},
		checkAndHandleIncoming() {
			let hasSessionId = window.Notary.NotarizationId != null && window.Notary.NotarizationId > 0;
			//we grab the URL parameters here as later the address in the browser may change. This way that info is not lost
			let urlParams = new URLSearchParams(document.location.search);
			let dataId = urlParams.get(`dataId`);
			let fileId = urlParams.get(`fileId`);
			let notarySource = urlParams.get(`notary_source`);
			let hasAutoUpload = dataId != null || fileId != null;

			if (hasSessionId) {
				this.loadDocuments().then(() => {
					this.handleAutoUpload(dataId, fileId);
				});						
			} else {
				//attempt to load the latest incomplete session
				this.loadLatestNotarization().then((notarizationId) => {
					//will return notarizationid if it loaded a session
					if (notarizationId != null && notarizationId > 0) { //session exists
						this.loadDocuments().then(() => {
							this.handleAutoUpload(dataId, fileId);
						});
					} else { //session does not exists
						if (hasAutoUpload) {
							//ensureNotarization will create a session, and we add a document to it
							this.ensureNotarization(notarySource).then(() => {
								this.doAutoUpload(dataId, fileId);
							});
						}
					}
				});
			}
		},
		handleAutoUpload(dataId, fileId) {			
			let hasAutoUpload = dataId != null || fileId != null;

			if (hasAutoUpload) {
				this.getDocumentIdIfAlreadyExists({ dataId, fileId }).then((documentId) => {
					if (documentId) {
						this.doDocumentRefresh(documentId).then(() => this.ItemTileFadeNow());
					}
					else {
						this.doAutoUpload(dataId, fileId);
					}
				});
			}
		},
		doAutoUpload(dataId, fileId) {
			let promise;
			let upload = {
				id: nanoid(),
				file: { name: ` ` }, //UploadManager requires a file name for it to display so setting to empty string.
				status: this.uploadStatusEnum.UPLOADING,
				message: `Adding file`
			};
			const component = `Auto Upload`;
			
			if (this.notarizationDocuments.length < 5) {
				if (dataId != null) {
					upload.type = this.uploadTypeEnum.LAWDEPOT;
					upload.file.id = parseInt(dataId);
					
					promise = lawDepot(upload.file.id, this.notarizationId);
				}
				if (fileId != null) {
					upload.type = this.uploadTypeEnum.MYDOCUMENTS;
					upload.file.id = parseInt(fileId);
					
					promise = myDocuments(upload.file.id, this.notarizationId);
				}
			} else {
				upload.type = this.uploadTypeEnum.UPLOAD;
				upload.file.id = parseInt(dataId) || parseInt(fileId);
				upload.status = this.uploadStatusEnum.ERROR;
				upload.file.name = `Document cannot be added.`;
				
				promise = this.submitUploadFailureEvent({ component, extra: `File limit` });
			}
			this.handleUploadPromise({ promise, upload });
			this.addUpload(upload);
		},
		
		checkIncomingStateParam() {
			let incomingPath = window.location.pathname;
			//find matched key in statesMap if exists in path
			const matchedStateKey = Object.keys(statesMap).find(key => incomingPath.toLowerCase().includes(key.toLowerCase()));
			if (matchedStateKey) {
				this.hasIncomingState = true;
				this.stateName = statesMap[matchedStateKey];
			}
		},
		...mapMutations([`addUpload`]),
		...mapActions([`handleUploadPromise`, `loadLatestNotarization`, `doDocumentRefresh`, `getDocumentIdIfAlreadyExists`, `ensureNotarization`, `submitUploadFailureEvent`]),
		...mapActions(`API`, [`goToCreateAccount`, `goToWhoIsSigning`, `goToSignersList`]),
		...mapActions([`loadDocuments`]),
	},
};
</script>